import {
  AdminBankV2Response,
  AdminWalletBankResponse,
  CompleteUserRequestData,
  CreateUserRequestData,
  GetAllUsersResponse,
  UpdateUserRequestData,
} from '../types/api/user.types';
import { SimpleLocationEntity } from '../types/location.types';
import { WalletBankResponse } from '../types/partner/api/wallet.partner-types';
import { Captcha, PaginationParams, ResponseType } from '../types/shared.types';
import { User, UserRole } from '../types/user.types';
import {
  AddBankV2Dto,
  AddBankV2DtoPartner,
  TransactionPending,
} from '../types/wallet.types';
import { api } from './api';

export const createUser = (data: CreateUserRequestData) => {
  return api.post<ResponseType<User>>('/v1/users', data);
};

export const getAllUsers = (
  params: PaginationParams<{
    query?: string | null;
    role?: UserRole | null;
    deleted?: boolean;
  }>
) =>
  api.get<ResponseType<GetAllUsersResponse[]>>('/v1/users', {
    params,
  });

export const getAllUsersWithRoles = (
  params: PaginationParams<{
    query?: string;
    roles?: Array<UserRole>;
    deleted?: boolean;
  }>
) =>
  api.get<ResponseType<GetAllUsersResponse[]>>('/v1/users/multi-roles', {
    params,
  });

export const deleteAccount = () => api.delete<ResponseType<null>>('/v1/users');

export const getLocationsByUserId = (userId: string | number) =>
  api.get<ResponseType<SimpleLocationEntity[]>>(
    `/v1/users/${userId}/locations`
  );

export const completeUser = (data: CompleteUserRequestData) =>
  api.put<ResponseType<User>>('/v1/users/complete', data);

export const updateUserById = async (
  userId: number | string,
  data: UpdateUserRequestData
) => api.patch<ResponseType<User>>(`/v1/users/${userId}`, data);

export const deleteUserById = async (userId: number | string) =>
  api.delete<ResponseType<null>>(`/v1/users/${userId}`);

export const recoverUserById = async (
  userId: number | string,
  recoverLocations?: boolean
) =>
  api.put<ResponseType<null>>(`/v1/users/${userId}/recover`, {
    recoverLocations,
  });

export const getAllUsersCount = (params: { query?: string | null }) =>
  api.get<ResponseType<{ deletedCount: number; nonDeletedCount: number }>>(
    '/v1/users/count',
    {
      params,
    }
  );

export const getUserById = async (userId: string | number) =>
  api.get<ResponseType<User>>(`/v1/users/${userId}`);

export const resendActivationEmail = async () =>
  api.post<ResponseType<null>>('/v1/users/resend-email');

export const acceptTermsAndPrivacy = async () =>
  api.put<ResponseType<User>>('/v1/users/terms-accepted');

/**
 * One row per DEVICE, not per user: the token identifies the browser, and the
 * backend drops any older row holding the same token before writing. That is
 * what lets the same device be re-used by a second account without the first
 * one keeping its push, and what makes a repeated registration a no-op.
 *
 * Returns the user with the full `fcmTokens` list - worth keeping, since it is
 * the only way to tell whether this device is already registered.
 */
export const updateUserFCMToken = async (token: string) =>
  api.put<ResponseType<User>>('/v1/users/fcm-token', { token, osType: 'web' });

export const changeNotificationStatus = async (enabled: boolean) =>
  api.put<ResponseType<User>>('/v1/users/notification-status', { enabled });

/**
 * Send this device's FCM token, always.
 *
 * With it, only this device is unsubscribed. WITHOUT it the backend drops
 * every token the user has, so signing out on a laptop would silently kill
 * push on their phone and tablet too - the empty-body form only still exists
 * for older clients.
 */
export const logoutUser = async (fcmToken?: string | null) =>
  api.post<ResponseType<null>>(
    '/v1/users/logout',
    fcmToken ? { fcmToken } : {}
  );

/** Every session and every device. Deliberately takes no token. */
export const logoutAllUsers = async () =>
  api.post<ResponseType<null>>('/v1/users/logout-all');

export const getUserBankAndBalanceInfo = async (userId: string | number) =>
  api.get<ResponseType<AdminWalletBankResponse>>(`/v1/users/bank/${userId}`);

export const addUserBank = async (
  userId: string | number,
  data: {
    customerId: string;
    fundingSourceId: string;
  }
) => api.post<ResponseType<null>>(`/v1/users/bank/${userId}`, data);

export const deleteBank = async (userId: string | number) =>
  api.delete<ResponseType<null>>(`/v1/users/bank/${userId}`);

export const deleteBankLocation = async (
  userId: string | number,
  locationId?: number
) =>
  api.delete<ResponseType<null>>(
    `/v1/users/bank/${userId}/location/${locationId}`
  );

export const increaseBankAmount = (
  bankId: string | number,
  data: {
    amount: number;
    adminPassword: string;
    captcha: Captcha;
    reason: string;
  },
  locationId?: number
) =>
  api.put<ResponseType<null>>(
    `/v1/users/bank/${bankId}/increase-balance`,
    data,
    {
      params: { locationId },
    }
  );

export const deductBankAmount = (
  bankId: string | number,
  data: {
    amount: number;
    adminPassword: string;
    captcha: Captcha;
    reason: string;
  },
  locationId?: number
) =>
  api.put<ResponseType<null>>(`/v1/users/bank/${bankId}/deduct-balance`, data, {
    params: { locationId },
  });

export const userAddBankAccountV2 = (
  userId: string | number,
  data: AddBankV2Dto
) =>
  api.post<ResponseType<WalletBankResponse>>(
    `/v1/users/bank/${userId}/v2`,
    data
  );

export const getUserTransactionPending = (
  userId: string | number,
  locationId?: string | number
) =>
  api.get<ResponseType<Array<TransactionPending>>>(
    `/v1/users/bank/${userId}/pending-transactions`,
    {
      params: { locationId },
    }
  );

export const getPartnerBankV2 = async (userId: string | number) =>
  api.get<ResponseType<AdminBankV2Response>>(
    `/v1/users/bank/${userId}/partner`
  );

export const userAddBankAccountV2Partner = (
  userId: string | number,
  data: AddBankV2DtoPartner
) =>
  api.post<ResponseType<WalletBankResponse>>(
    `/v1/users/bank/${userId}/v2/partner`,
    data
  );
